import { Link } from "react-router-dom";
import { Home, Shield, FileCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  {
    icon: Home,
    title: "Mortgage",
    desc: "We know it can be confusing and difficult to understand the mortgage marketplace. Different providers use complicated jargon and various criteria to make decisions, and comparing plans can be a minefield to wrap your head around. Let us take care of it for you.",
    features: [
      "First-time buyer mortgages",
      "Remortgaging solutions",
      "Buy-to-let mortgages",
      "Help to Buy schemes",
      "Shared Ownership",
      "Right to Buy & Acquire",
    ],
    image: "https://images.unsplash.com/photo-1680576554984-efa06cb96392?w=800&q=80",
    link: "/services/mortgage",
  },
  {
    icon: Shield,
    title: "Home Insurance",
    desc: "Home insurance protects your home and its belongings. It can cover the cost of repairing or rebuilding your home if it's damaged by things like fire, storm or flood. And it can cover the cost of replacing the things in your home if they're stolen, or repairing or replacing them if they're damaged.",
    features: [
      "Buildings insurance",
      "Contents insurance",
      "Combined policies",
      "Accidental damage cover",
      "Legal expenses cover",
      "Emergency home cover",
    ],
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80",
    link: "/services/home-insurance",
  },
  {
    icon: FileCheck,
    title: "Protection",
    desc: "At KK Mortgage Solutions, we only work with providers that we trust – they have an established history of paying out in the event of damages or theft, they can be relied on to give the coverage that you need, and they are excellent at communicating with clarity and brevity.",
    features: [
      "Life insurance",
      "Critical illness cover",
      "Income protection",
      "Mortgage protection",
      "Family income benefit",
      "Key person insurance",
    ],
    image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80",
    link: "/services/protection",
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero Banner */}
      <section className="relative bg-[#111111] py-32 lg:py-40">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')] bg-cover bg-center opacity-15" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">
            Services
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            Helping You Achieve Your<br />
            <span className="text-[#d64241]">Homeownership Goals</span>
          </h1>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          {services.map((service, i) => (
            <div
              key={service.title}
              data-testid={`service-detail-${service.title.toLowerCase().replace(' ', '-')}`}
              className={`grid lg:grid-cols-2 gap-16 items-center ${
                i % 2 === 1 ? "lg:direction-rtl" : ""
              }`}
            >
              <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="w-14 h-14 rounded-xl bg-[#fef2f2] flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-[#d64241]" />
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold text-black tracking-tight mb-6">
                  {service.title}
                </h2>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8 font-body">
                  {service.desc}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {service.features.map((feature, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#d64241] flex-shrink-0" />
                      <span className="text-sm text-gray-700 font-body">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link to={service.link}>
                  <Button
                    data-testid={`service-cta-${service.title.toLowerCase().replace(' ', '-')}`}
                    className="bg-[#d64241] hover:bg-[#b53736] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-[#d64241]/25 hover:-translate-y-0.5 transition-all duration-200"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.1)]">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-[350px] lg:h-[450px] object-cover"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-[#111111]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-6">
            Not Sure Which Service You Need?
          </h2>
          <p className="text-lg text-gray-300 mb-10 font-body">
            Get in touch with our expert team for a free consultation. We'll help you find the
            perfect solution for your needs.
          </p>
          <Link to="/contact">
            <Button
              data-testid="services-cta-btn"
              className="bg-[#d64241] hover:bg-[#b53736] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-[#d64241]/25 hover:-translate-y-0.5 transition-all duration-200"
            >
              Contact Us Today
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
